import { CREATE_USER_FEATURE_FROM_QUESTION } from '../../mutations/UserFeature';
import { FeatureModel } from './FeatureModel';
import { GET_FEATURE_BY_ID } from '../../queries/Feature';
import { Instance, cast, flow, getParent, getRoot, types } from 'mobx-state-tree';
import { clientFunction } from '../../../config/apollo/client';
import { filterDefinedFields } from '../../../utils/filterFields';
import { getMax } from '../../../components/QuestionSessions/QuestionSession/utils';

export const FeatureStore = types
  .model('FeatureStore', {
    feature: types.array(FeatureModel),
  })
  .actions(self => {

    const loadFeatureFromUserFeature = (feature: any, language?: string) => {
      if(feature?._id) {
        feature.feature = feature._id;
      }
      delete feature._id;
      const languageData= language? language: (getParent(self) as any).language.name.key;
      const featureData = { ...feature, language: languageData};
      const index = self.feature?.findIndex(c => c.feature === feature.feature);
      if (index === -1) {
        self.feature?.push(featureData);
      } else {
        self.feature[index] = featureData;
      }
    };

    const loadFeature = (feature: any) => {
      let featData =  {} as any;
      if(feature._id) {
        featData.feature = feature._id;
      }
      delete feature._id;
      featData = {...featData, ...filterDefinedFields(feature, FeatureModel.properties)};
      if(!featData.feature) { return;}
      // no score yet for this user: keep the feature without a value
      const userFeature = feature.userFeature || {};
      featData = {...featData,
        language: (getParent(self) as any).language.name.key,
        value: userFeature.value,
        color: userFeature.interval?.color,
        name: userFeature.interval?.label?.name,
        description: userFeature.interval?.label?.description,
        descriptionForSurvey: userFeature.interval?.label?.descriptionForSurvey,
        logo: userFeature.interval?.logo};

      const index = self.feature?.findIndex(c => c.feature === featData.feature);
      if (index === -1) {
        self.feature?.push(featData);
      } else {
        self.feature[index] = featData;
      }
    };

    const fetchFeature = flow(function*  (id: string) {
      try {
        const language = (getParent(self) as any).language.name.key;
        const { data } = yield clientFunction(language).query({
          query: GET_FEATURE_BY_ID,
          variables: { id },
          fetchPolicy: 'no-cache'
        });
        if (data?.feature) {
          loadFeature(data.feature);
          return;
        }
        return null;
      } catch (error) {
        console.error('Error fetching feature:', error);
        return null;
      }
    });

    const updateWithVal = (featureId: string, val: number) => {
      const index = self.feature?.findIndex(c => c.feature === featureId);
      if (index === -1) {
        self.feature?.push({feature: featureId, value: val, type: 'QUESTION'});
      } else {
        self.feature[index] = {...self.feature[index], value: val, type: 'QUESTION'};
      }
    };
    const updateWithFreeText = (featureId: string, val: string) => {
      const index = self.feature?.findIndex(c => c.feature === featureId);
      if (index === -1) {
        self.feature?.push({feature: featureId, freeTextVal: val, type: 'QUESTION'});
      } else {
        self.feature[index] = {...self.feature[index], freeTextVal: val, type: 'QUESTION'};
      }
    };
    const updateWithAnswers = (featureId: string, answers: string[]) => {
      const index = self.feature?.findIndex(c => c.feature === featureId);
      if (index === -1) {
        self.feature?.push({feature: featureId, answerValues: answers, type: 'QUESTION'});
      } else {
        self.feature[index] = {...self.feature[index], answerValues: answers, type: 'QUESTION'};
      }
    };
    const createFeatureFromQuestion: (
      featureId: string,
      questionId: string,
      reckoningId: string | null,
      value: any,
    ) => Promise<void> = flow(function*(featureId, questionId, reckoningId, value) {
      // TODO make this idempotent on serverside
      yield clientFunction(getRoot<any>(self).language.name.key).mutate({
        mutation: CREATE_USER_FEATURE_FROM_QUESTION,
        fetchPolicy:'no-cache',
        variables: {
          feature: featureId,
          question: questionId,
          reckoning: reckoningId,
          ...value
        }
      });
    });

    const updateWithSonar = (featureId: string, value: number, importance: number ) => {
      const index = self.feature?.findIndex(c => c.feature === featureId);
      const data = {
        value,
        importance,
        type: 'SONAR'
      };
      if (index === -1) {
        self.feature?.push({feature: featureId, ...data});
      } else {
        self.feature[index] = {...self.feature[index], ...data};
      }
    };
    const clear = () => {
      self.feature = cast([]);
    };

    return {
      loadFeatureFromUserFeature,
      updateWithFreeText,
      updateWithVal,
      updateWithAnswers,
      updateWithSonar,
      fetchFeature,
      createFeatureFromQuestion,
      clear
    };
  })
  .views(self => ({
    getFeature(id: string) {
      if (self.feature) {
        const index = self.feature.findIndex(p => p.feature === id);
        if (index === -1) {
          return null;
        } else {
          return self.feature[index];
        }

      } else {
        return null;
      }

    },
    getLabel(id: string, value: number): string {
      const question = this.getQuestion(id);
      if (question && question.range && question.range.intervals && question.range.intervals.length > 0) {
        const maxValue = getMax(question.range.intervals);
        const interval = question.range.intervals.find(interval => {
          return (
            (interval.minValue <= value && interval.maxValue > value) ||
            (interval.maxValue === maxValue && maxValue === value)
          );
        });
        if (interval) {
          return interval.label.name;
        } else {
          return '';
        }
      } else {
        return '';
      }
    }
  }));

export type IFeatureStore = Instance<typeof FeatureStore>;
